import PermissionButton from 'components/buttons/PermissionButton';
import Loader from 'components/Loader';
import ExpenseModal from 'components/modals/expenses/ExpenseModal';
import { useModal } from 'components/modals/useModal';
import { Tooltip } from 'components/ui/tooltip';
import useAuth from 'hooks/useAuth.ts';
import isEqual from 'lodash/isEqual';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { ClassifierData } from 'types/classifiers';
import { EmployeeListItemMinDto } from 'types/employees';
import { ExpenseDto, ExpenseForm, ExpenseFormData } from 'types/expenses';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { getOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { useExpenseEntryDisabled } from 'utils/useExpenseEntryDisabled';
import ExpenseItem from './ExpenseIItem';

type Props = {
    expenses: ExpenseDto[];
    classifiers: Record<string, ClassifierData>;
    timecardData: { id?: number | null; matterId?: number | null };
    disableAdd?: boolean;
    onChange?: (args: ExpenseDto[]) => void;
};

const TimeCardExpenseList: React.FC<Props> = ({
    expenses,
    classifiers,
    timecardData,
    disableAdd = false,
    onChange,
}) => {
    const { user } = useAuth();
    const userId = user?.id;
    const isExpenseEntryDisabled = useExpenseEntryDisabled();

    const [expenseFormDataList, setExpenseFormDataList] = useState<ExpenseFormData[]>(expenses);

    const { openModal, closeModal } = useModal();

    const { data: currentEmployeeQueryData, isLoading: isLoadingCurrentEmployeeQueryData } =
        useGetEmployeesListWithFiltersQuery({ pageSize: 1, page: 0, filters: `id==${userId}` }, { skip: !userId });

    const currentEmployeeData = useMemo<EmployeeListItemMinDto | undefined>(() => {
        return currentEmployeeQueryData?.content.at(0);
    }, [currentEmployeeQueryData]);

    const handleExpenseChange = useCallback(
        (newExpenses: ExpenseFormData[]) => {
            setExpenseFormDataList(newExpenses);
            if (onChange) {
                const mappedData = mapFormDataToDto(newExpenses);
                if (!isEqual(expenses, mappedData)) {
                    onChange(mappedData);
                }
            }
        },
        [expenses, onChange]
    );

    const updateExpenseList = useCallback(
        (newExpense_: ExpenseForm, id?: number) => {
            const newExpense = mapFormToOldFormDto(newExpense_, id);
            let alreadyExists = false;
            let orderId = 0;
            const newExpenses = expenseFormDataList.map((exp) => {
                orderId += 1;
                if (
                    (exp?.id && id && exp?.id === id) ||
                    (exp?.orderId && newExpense?.orderId && exp?.orderId === newExpense?.orderId)
                ) {
                    alreadyExists = true;
                    return { ...exp, ...newExpense, orderId: orderId, isEdited: true };
                }
                return { ...exp, orderId: orderId };
            });

            if (!alreadyExists) {
                orderId += 1;

                newExpenses.push({ ...newExpense, orderId, isCreated: !alreadyExists, isEditable: true });
            }
            handleExpenseChange(newExpenses);
        },
        [expenseFormDataList, handleExpenseChange]
    );

    const openExpenseModal = useCallback(
        (expense?: ExpenseDto & { orderId?: number }): void => {
            if (disableAdd || (!expense && isExpenseEntryDisabled)) {
                return;
            }
            const preSelectedEmployee = currentEmployeeData;

            const initalData: ExpenseForm = {
                id: expense?.id,
                externalId: expense?.externalId || null,
                expenseType:
                    (
                        getOptionsFromClassifier(classifiers['EXPENSE_TYPE']) as {
                            label: string;
                            value: string;
                        }[]
                    ).find((val) => val.value === expense?.expenseTypeCode) ?? null,
                currencyType:
                    (
                        getOptionsFromClassifier(classifiers['CURRENCY']) as {
                            label: string;
                            value: string;
                        }[]
                    ).find((val) => val.value === expense?.originalCurrencyTypeCode) ?? null,
                description: expense?.description ?? '',
                expenseAmount: expense?.expenseAmountOriginalCurrency ?? '',
                expenseDate: expense?.expenseDate
                    ? formatDateStringInput(new Date(expense?.expenseDate))
                    : formatDateStringInput(new Date()),
                isNonBillable:
                    expense?.isNonBillable !== undefined && expense?.isNonBillable !== null
                        ? expense.isNonBillable
                        : false,
                employee: expense?.employee
                    ? expense.employee
                    : preSelectedEmployee
                      ? { id: preSelectedEmployee?.id, name: preSelectedEmployee?.name }
                      : null,
                matter: null, // possibly preselected at ExpenseModal component level
                timecardId: null,
                effortId: expense?.effortId ?? null,
                orderId: expense?.orderId ?? undefined,
                createdAt: expense?.createdAt,
                createdByEmployee: expense?.createdByEmployee,
                lastModifiedAt: expense?.lastModifiedAt,
                changedByEmployee: expense?.changedByEmployee,
            };

            const isReadOnly: boolean = !!expense && (!expense.isEditable || isExpenseEntryDisabled);

            openModal(
                <ExpenseModal
                    onChangeRaw={(val) => updateExpenseList(val, expense?.id)}
                    initialData={initalData}
                    onClose={closeModal}
                    disableSelectMatter
                    titleId={expense ? 'expenses.edit' : 'expenses.add'}
                    readOnly={isReadOnly}
                    preselectMatterId={(timecardData?.matterId || expense?.matter?.id) ?? undefined}
                />,
                closeModal
            );
        },
        [
            classifiers,
            timecardData,
            disableAdd,
            closeModal,
            openModal,
            updateExpenseList,
            currentEmployeeData,
            isExpenseEntryDisabled,
        ]
    );

    const addButton = (
        <span>
            <PermissionButton
                permissions={['CREATE.EXPENSES']}
                variant="outlined"
                className="w-fit"
                size="small"
                onClick={() => openExpenseModal()}
                disabled={isExpenseEntryDisabled}
            >
                <FormattedMessage id="expenses.add-new-expense" />
            </PermissionButton>
        </span>
    );

    if (isLoadingCurrentEmployeeQueryData) {
        return <Loader />;
    }

    return (
        <div className="flex w-full flex-col gap-4">
            {expenseFormDataList.map((expense) => (
                <ExpenseItem
                    key={expense.id}
                    classifiers={classifiers}
                    expense={expense}
                    handleEdit={() => openExpenseModal(expense)}
                    readOnly={!expense.isEditable || isExpenseEntryDisabled}
                    disableView={disableAdd}
                />
            ))}
            {!disableAdd &&
                (isExpenseEntryDisabled ? (
                    <Tooltip content={t('expenses.integration-entry-disabled')}>{addButton}</Tooltip>
                ) : (
                    addButton
                ))}
        </div>
    );
};

export default TimeCardExpenseList;

const mapFormDataToDto = (expenses: ExpenseFormData[]): ExpenseDto[] =>
    expenses.map((expense) => {
        const { matterLabel, employeeLabel, expenseTypeCodeLabel, currencyTypeCodeLabel, ...rest } = expense;
        return { ...rest };
    });

const mapFormToOldFormDto = (expense: ExpenseForm, id?: number): ExpenseFormData => {
    return {
        id: id ?? 0,
        description: expense.description,
        isNonBillable:
            expense?.isNonBillable !== undefined && expense?.isNonBillable !== null ? expense.isNonBillable : false,
        matter: expense?.matter ?? null,
        timecardId: expense?.timecardId ?? null,
        employeeId: expense?.employee?.id ?? null,
        expenseTypeCode: expense?.expenseType?.value ?? undefined,
        expenseAmountOriginalCurrency: expense?.expenseAmount ?? null,
        originalCurrencyTypeCode: expense?.currencyType?.value ?? undefined,
        expenseDate: expense.expenseDate ?? new Date().toDateString(),
        orderId: expense?.orderId ?? undefined,
    };
};
